import { get, isEqual, isFunction, isNil, map, omit } from 'lodash';
import { AbstractType, InjectionToken, Injector, Type } from '@angular/core';

import { Threshold } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue/inventory-item-venue';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import {
  IQohInventoryItemVenue,
  REPLENISH_TYPE_VALUES,
} from '@app/shared/types';
import { UiActions } from '@app/store/actions/ui/ui.actions';

import { ReplenishDataServiceProvider } from '../replenish-data/replenish-data-service.provider';
import {
  IReplenishDestination,
  IReplenishDestParams,
  IReplenishStrategyParams,
  ReplenishDestItemParams,
  ReplenishDestItemsParams,
  ReplenishSingleDestParams,
} from '../replenish.types';
import {
  dispatchCallbackEvent,
  getItemThreshold,
  isSkipReplenish,
} from '../replenish.utils';
import { IReplenishStrategy } from './replenish-strategy.types';
import { ReplenishInventoryDataService } from '../replenish-data/replenish-inventory-data.service';
import { REPLENISH_APPLY_ITEMS_SCOPE } from '../replenish.enums';

abstract class AbstractReplenishStrategy<
  T extends IReplenishDestination = IReplenishDestination,
> implements IReplenishStrategy<T>
{
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(ReplenishDataServiceProvider)
  public replenishDataServiceProvider: ReplenishDataServiceProvider;
  @lazyInject(ReplenishInventoryDataService)
  public replenishInventoryDataService: ReplenishInventoryDataService;

  public constructor(public injector: Injector) {}

  public inject<S>(
    token: Type<S> | InjectionToken<S> | AbstractType<S>,
    notFoundValue?: S,
  ): S {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  protected abstract replenishDestinationItem(
    params: ReplenishDestItemParams,
  ): IQohInventoryItemVenue;

  /**
   * replenish destination items method, you can implement this to use different calc method here
   *
   * @param params calc params
   * @returns calc result
   */
  protected replenishDestinationItems(
    params: ReplenishDestItemsParams,
  ): IQohInventoryItemVenue[] {
    const { items, additionalData, replenishApplyItemsScope } = params;
    const replenishType = REPLENISH_TYPE_VALUES.PAR;

    const event = get(additionalData, 'event');

    return map(items, (item) => {
      const itemThreshold: Threshold = getItemThreshold(item, event);

      if (isNil(get(itemThreshold, replenishType))) {
        return item;
      }

      if (
        isEqual(
          replenishApplyItemsScope,
          REPLENISH_APPLY_ITEMS_SCOPE.CHARGEABLE_ONLY,
        ) &&
        !item.is_chargeable
      ) {
        return item;
      }

      if (
        isEqual(
          replenishApplyItemsScope,
          REPLENISH_APPLY_ITEMS_SCOPE.NON_CHARGEABLE_ONLY,
        ) &&
        item.is_chargeable
      ) {
        return item;
      }

      return this.replenishDestinationItem({
        item,
        replenishType,
        percent: params.percent,
        threshold: itemThreshold,
        additionalData: params.additionalData,
      });
    });
  }

  /**
   * replenish single destination
   *
   * @param params replenish params
   * @returns replenish result
   */
  protected abstract replenishDestination(
    params: ReplenishSingleDestParams,
  ): IReplenishDestination;

  protected replenishDestinations(
    params: IReplenishDestParams,
  ): IReplenishDestination[] {
    const { percentDesMap, on } = params;

    return map(percentDesMap, (destination) => {
      // #TODO is need this or is need remove skip replenish judgement?
      if (isSkipReplenish(destination)) {
        return destination;
      }

      const destinationReplenishResult = this.replenishDestination({
        ...omit(params, 'percentDesMap'),
        destination,
      });

      dispatchCallbackEvent(on, 'afterEachReplenish', {
        replenishParams: omit(params, ['percentDesMap', 'on']),
        destination,
      });

      return destinationReplenishResult;
    });
  }

  public async replenish(params: IReplenishStrategyParams): Promise<T | T[]> {
    const replenishResult = this.replenishDestinations(params);

    return Promise.resolve(replenishResult as any);
  }
}

export { AbstractReplenishStrategy };
